import React, { CSSProperties } from 'react';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

/**
 * Derives up-to-two-letter initials. Accepts either a single `name` string
 * (first + last whitespace-separated token) or discrete `firstName`/`lastName`.
 */
export function getInitials({ name, firstName, lastName }: { name?: string; firstName?: string; lastName?: string }): string {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
    return `${first}${last}`.toUpperCase();
  }
  const first = firstName?.trim()?.[0] ?? '';
  const last = lastName?.trim()?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

export interface InitialsAvatarProps {
  name?: string;
  firstName?: string;
  lastName?: string;
  /** Fixed pixel size — sets width/height (and a derived font-size unless `fontSize` is given). */
  size?: number;
  /** Explicit font-size (px number or any CSS length). Overrides the size-derived default. */
  fontSize?: number | string;
  /** Absolutely fill a positioned parent (width/height 100%, inset 0). */
  fill?: boolean;
  /** Circular avatar (border-radius 50%). For other radii, style via `className`. */
  circle?: boolean;
  /** Subtle 1px border. */
  bordered?: boolean;
  /** Decorative empty placeholder — renders no initials and is hidden from assistive tech. */
  decorative?: boolean;
  className?: string;
}

/**
 * Presentational fallback avatar that renders a person's initials on the shared
 * grey/navy scheme when no profile photo is available. Owns initials derivation,
 * markup and the invariant colour scheme; callers control size/shape via props or
 * a `className`.
 */
export default function InitialsAvatar({
  name,
  firstName,
  lastName,
  size,
  fontSize,
  fill = false,
  circle = false,
  bordered = false,
  decorative = false,
  className,
}: InitialsAvatarProps) {
  const initials = getInitials({ name, firstName, lastName });
  const label = name ?? [firstName, lastName].filter(Boolean).join(' ');

  const style: CSSProperties = {};
  if (!fill && size != null) {
    style.width = size;
    style.height = size;
  }
  if (fontSize != null) {
    style.fontSize = fontSize;
  } else if (size != null) {
    style.fontSize = Math.round(size * 0.36);
  }

  return (
    <div
      className={buildClassName(
        styles['initials-avatar'],
        fill && styles.fill,
        circle && styles.circle,
        bordered && styles.bordered,
        className
      )}
      style={style}
      aria-label={decorative ? undefined : label || undefined}
      aria-hidden={decorative || undefined}
    >
      {!decorative && <span>{initials}</span>}
    </div>
  );
}
